// ------------------ Imports ------------------
import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  Tooltip,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  AccountCircle,
  Chat,
  Settings,
  Logout,
  Timeline,
  Download,
  Menu as MenuIcon,
} from "@mui/icons-material";
import jsPDF from "jspdf";
import { styled } from "@mui/material/styles";
import { updatePassword } from "firebase/auth";
import { auth } from "./firebase";
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
} from "reactflow";
import "reactflow/dist/style.css";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import api from "./utils/api";

const drawerWidth = 80;

// ------------------ Styles ------------------
const ChatWindow = styled(Paper)(() => ({
  borderRadius: 20,
  padding: 20,
  height: "70vh",
  display: "flex",
  flexDirection: "column",
  background: "rgba(255, 255, 255, 0.85)",
  boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
}));

const ChatBubble = styled(Box)(({ from }) => ({
  maxWidth: "70%",
  padding: "12px 16px",
  borderRadius: 16,
  marginBottom: 12,
  alignSelf: from === "user" ? "flex-end" : "flex-start",
  background: from === "user" ? "#0a9396" : "#f1f5f9",
  color: from === "user" ? "#fff" : "#222",
  fontWeight: 500,
  lineHeight: 1.5,
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  whiteSpace: "pre-line",
  wordBreak: "break-word",
  cursor: "default",
}));

// ------------------ Roadmap Flow ------------------
function getStreamGroup(step) {
  const txt = (step.step || "").toLowerCase();
  if (txt.includes("science")) return "Science";
  if (txt.includes("commerce")) return "Commerce";
  if (txt.includes("arts") || txt.includes("humanities")) return "Arts";
  return "General";
}

function CareerFlow({ roadmap }) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    if (!roadmap || roadmap.length === 0) return;

    let streamTag = "General";
    const roadmapWithStreams = roadmap.map((item) => {
      const group = getStreamGroup(item);
      if (group !== "General") streamTag = group;
      return { ...item, stream: streamTag };
    });

    const uniqueStreams = [...new Set(roadmapWithStreams.map((step) => step.stream))];
    const streamYMap = {};
    uniqueStreams.forEach((s, i) => {
      streamYMap[s] = 60 + i * 170;
    });

    const generatedNodes = roadmapWithStreams.map((item, idx) => ({
      id: `node-${idx}`,
      data: {
        label: (
          <div style={{ padding: 10, textAlign: "center" }}>
            <strong>{item.step}</strong>
          </div>
        ),
      },
      position: { x: 250 * idx, y: streamYMap[item.stream] },
      style: {
        border: "2px solid #0a9396",
        borderRadius: 10,
        padding: 10,
        background: "#e0f7f7",
        minWidth: 180,
      },
    }));

    const generatedEdges = [];
    for (let i = 1; i < roadmapWithStreams.length; i++) {
      generatedEdges.push({
        id: `edge-${i - 1}`,
        source: `node-${i - 1}`,
        target: `node-${i}`,
        animated: true,
        style: { stroke: "#0a9396" },
      });
    }

    setNodes(generatedNodes);
    setEdges(generatedEdges);
  }, [roadmap, setNodes, setEdges]);

  return (
    <div style={{ height: "70vh", width: "100%", borderRadius: 12 }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        fitView
        nodesDraggable={false}
        nodesConnectable={false}
      >
        <MiniMap />
        <Controls />
        <Background color="#aaa" gap={16} />
      </ReactFlow>
    </div>
  );
}

// ------------------ Main Dashboard ------------------
export default function UserDashboardPro() {
  const { currentUser, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [question, setQuestion] = useState("");
  const [roadmap, setRoadmap] = useState([]);
  const [chatHistory, setChatHistory] = useState([
    { from: "bot", text: "👋 Hi! What career interests do you have? You can also say 'not decided'." },
  ]);
  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = useState("Chat");
  const bottomRef = useRef(null);
  const navigate = useNavigate();

  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // ------------------ Logout ------------------
  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  // ------------------ Change Password ------------------
  const handleChangePassword = async () => {
    const user = auth.currentUser;
    if (!user) return alert("⚠️ You need to be logged in.");
    try {
      await updatePassword(user, newPassword);
      alert("✅ Password updated successfully!");
      setNewPassword("");
      setOpenPasswordDialog(false);
    } catch (error) {
      console.error("Error updating password:", error);
      if (error.code === "auth/requires-recent-login") {
        alert("⚠️ Please log out and log in again to change password.");
      } else {
        alert("⚠️ Failed to change password: " + error.message);
      }
    }
  };

  // ------------------ Auto-scroll ------------------
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory]);

  // ------------------ Chat Submit ------------------
  const handleSubmit = async () => {
    if (!question.trim()) return;
    setChatHistory((prev) => [...prev, { from: "user", text: question }]);
    setQuestion("");
    setLoading(true);

    try {
      const res = await api.post("/api/chat", { message: question });
      const data = res.data;

      setChatHistory((prev) => [
        ...prev,
        { from: "bot", text: data.reply || "Sorry, I could not answer that." },
      ]);

      if (Array.isArray(data.roadmapUpdates)) {
        // Ensure "10th Class" is present but not duplicated
        let steps = [...data.roadmapUpdates];
        if (!steps.some(s => s.step.toLowerCase().includes("10th"))) {
          steps.unshift({ step: "10th Class", description: "Complete high school with focus subjects." });
        }
        setRoadmap(steps);
      }
    } catch (error) {
      setChatHistory((prev) => [
        ...prev,
        { from: "bot", text: `⚠️ ${error.response?.data?.error || "Server error. Try again later."}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // ------------------ Download PDF ------------------
  const handleDownload = () => {
    const doc = new jsPDF();
    doc.setFont("helvetica", "bold");
    doc.text("CareerPath Chat & Roadmap", 10, 10);

    let y = 20;
    doc.setFont("helvetica", "normal");
    doc.text("Chat History:", 10, y);
    y += 10;
    chatHistory.forEach((msg) => {
      const prefix = msg.from === "user" ? "You: " : "Bot: ";
      doc.text(`${prefix}${msg.text}`, 10, y, { maxWidth: 180 });
      y += 10;
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
    });

    y += 10;
    doc.text("Roadmap:", 10, y);
    y += 10;
    roadmap.forEach((stepObj, idx) => {
      doc.text(`${idx + 1}. ${stepObj.step} - ${stepObj.description}`, 10, y);
      y += 10;
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
    });

    doc.save("careerpath.pdf");
  };

  // ------------------ Send Email ------------------
  const handleSendEmail = async () => {
    if (!currentUser?.email) return alert("Please log in to send reports.");
    setSendingEmail(true);

    try {
      const emailBody = `
CareerPath Report

Chat History:
${chatHistory.map(msg => `${msg.from === "user" ? "You" : "Bot"}: ${msg.text}`).join("\n")}

Roadmap:
${roadmap.map((r, i) => `${i + 1}. ${r.step} - ${r.description}`).join("\n")}
`;

      const res = await api.post("/api/send-report", {
        to: currentUser.email,
        subject: "Your CareerPath Report",
        message: emailBody,
      });

      if (res.data.success) alert(res.data.message || "Report sent successfully!");
      else alert(res.data.message || "Failed to send report.");
    } catch (err) {
      console.error(err);
      alert(`⚠️ ${err.response?.data?.error || "Server error while sending email."}`);
    } finally {
      setSendingEmail(false);
    }
  };

  // ------------------ Mobile Drawer Toggle ------------------
  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  const drawer = (
    <>
      <Toolbar />
      <List>
        {[
          { text: "Chat", icon: <Chat /> },
          { text: "Roadmap", icon: <Timeline /> },
        ].map((item, i) => (
          <Tooltip key={i} title={item.text} placement="right">
            <ListItem
              button
              onClick={() => {
                setActivePage(item.text);
                if (isMobile) setMobileOpen(false);
              }}
              sx={{
                justifyContent: "center",
                py: 2,
                bgcolor:
                  activePage === item.text ? "rgba(255,255,255,0.2)" : "transparent",
                borderRadius: 2,
                mx: 1,
              }}
            >
              <ListItemIcon sx={{ color: "#fff", minWidth: "unset" }}>{item.icon}</ListItemIcon>
            </ListItem>
          </Tooltip>
        ))}
      </List>
    </>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f8fafc" }}>
      {/* Topbar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: 1201,
          background: "#fff",
          color: "#111",
          borderBottom: "1px solid #e5e7eb",
          ml: isMobile ? 0 : `${drawerWidth}px`,
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            CareerPath
          </Typography>
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
            <Avatar sx={{ bgcolor: "#0a9396" }}>
              <AccountCircle />
            </Avatar>
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <MenuItem disabled>{currentUser?.email || "Guest User"}</MenuItem>
            <MenuItem
              onClick={() => {
                setOpenPasswordDialog(true);
                setAnchorEl(null);
              }}
            >
              <Settings fontSize="small" sx={{ mr: 1 }} /> Change Password
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Logout fontSize="small" sx={{ mr: 1 }} /> Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawers */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              boxSizing: "border-box",
              background: "#0a9396",
              color: "#fff",
              alignItems: "center",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      )}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              boxSizing: "border-box",
              background: "#0a9396",
              color: "#fff",
              alignItems: "center",
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Main */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 4,
          ml: isMobile ? 0 : `${drawerWidth}px`,
          mt: 8,
        }}
      >
        {activePage === "Chat" && (
          <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" } }}>
            <Box sx={{ flex: 2, display: "flex", flexDirection: "column" }}>
              <Typography variant="h5" fontWeight={700} mb={2}>
                Roadmap Generator
              </Typography>
              <ChatWindow>
                <Box sx={{ flex: 1, overflowY: "auto" }}>
                  {chatHistory.map((msg, i) => (
                    <ChatBubble key={i} from={msg.from}>
                      {msg.text}
                    </ChatBubble>
                  ))}
                  {loading && (
                    <Typography align="center" sx={{ mt: 2 }}>
                      <CircularProgress size={20} sx={{ mr: 1 }} /> Thinking...
                    </Typography>
                  )}
                  <div ref={bottomRef} />
                </Box>
                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  <TextField
                    placeholder="Type your question..."
                    fullWidth
                    multiline
                    maxRows={3}
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    disabled={loading}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 20, bgcolor: "#fff" } }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit();
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    sx={{
                      bgcolor: "#0a9396",
                      fontWeight: 600,
                      borderRadius: 20,
                      px: 3,
                      "&:hover": { bgcolor: "#005f73" },
                    }}
                    onClick={handleSubmit}
                    disabled={loading || !question.trim()}
                  >
                    Ask
                  </Button>
                </Box>
              </ChatWindow>
            </Box>
          </Box>
        )}

        {activePage === "Roadmap" && (
          <Box>
            <Typography variant="h5" fontWeight={700} mb={2}>
              Career Roadmap
            </Typography>
            <CareerFlow roadmap={roadmap} />
            <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
              <Button
                variant="contained"
                startIcon={<Download />}
                sx={{ bgcolor: "#0a9396" }}
                onClick={handleDownload}
              >
                Download PDF
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Change Password Dialog */}
      <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)}>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <TextField
            label="New Password"
            type="password"
            fullWidth
            margin="normal"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPasswordDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleChangePassword}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
